import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Layers, Database } from 'lucide-react';

const heroPoints = [
  {
    icon: MessageSquare,
    text: 'Just ask in plain language and get back insights.',
  },
  {
    icon: Layers,
    text: 'No specialized software, no wrangling formats.',
  },
  {
    icon: Database,
    text: 'Connect your own data and seamlessly combine with planetary-scale data.',
  },
];

const GRID_CELL = 40;
const GRID_COLS = 3;
const GRID_ROWS = 4;
const GRID_PALETTE = [
  'rgba(6, 78, 59, 0.55)',
  'rgba(4, 120, 87, 0.42)',
  'rgba(13, 148, 136, 0.38)',
  'rgba(5, 150, 105, 0.34)',
  'rgba(17, 94, 89, 0.48)',
  'rgba(6, 95, 70, 0.4)',
  'rgba(20, 83, 45, 0.5)',
  'rgba(15, 118, 110, 0.36)',
  'rgba(2, 44, 34, 0.58)',
  'rgba(19, 78, 74, 0.44)',
  'rgba(4, 47, 46, 0.52)',
  'rgba(13, 116, 96, 0.4)',
];

const AuthGridBackground = () => {
  const patternWidth = GRID_COLS * GRID_CELL;
  const patternHeight = GRID_ROWS * GRID_CELL;
  const cells = Array.from({ length: GRID_ROWS * GRID_COLS }, (_, index) => {
    const col = index % GRID_COLS;
    const row = Math.floor(index / GRID_COLS);
    const paletteIndex = index % GRID_PALETTE.length;
    const fillA = GRID_PALETTE[paletteIndex];
    const fillB = GRID_PALETTE[(paletteIndex + 4) % GRID_PALETTE.length];
    const fillC = GRID_PALETTE[(paletteIndex + 7) % GRID_PALETTE.length];

    return (
      <rect
        key={index}
        x={col * GRID_CELL}
        y={row * GRID_CELL}
        width={GRID_CELL}
        height={GRID_CELL}
        fill={fillA}
        stroke="rgba(52, 211, 153, 0.14)"
        strokeWidth="1"
      >
        <animate
          attributeName="fill"
          values={`${fillA};${fillB};${fillC};${fillA}`}
          dur={`${7 + (index % 5) * 1.5}s`}
          repeatCount="indefinite"
        />
      </rect>
    );
  });

  return (
    <svg
      className="absolute inset-0 h-full w-full"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect width="100%" height="100%" fill="#030712" />
      <defs>
        <pattern
          id="auth-grid"
          width={patternWidth}
          height={patternHeight}
          patternUnits="userSpaceOnUse"
        >
          {cells}
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#auth-grid)" />
    </svg>
  );
};

/**
 * Split-screen layout for all authentication pages.
 * Left third: grid hero background + brand (desktop only).
 * Right two-thirds: centered form content, optional footer.
 * Pass `wide` for forms with more fields (e.g. access request).
 */
const AuthLayout = ({ children, footer, wide = false }) => {
  return (
    <div className="min-h-screen flex bg-white dark:bg-gray-950">
      <aside className="relative hidden lg:flex lg:w-1/3 shrink-0 flex-col justify-between overflow-hidden p-10">
        <AuthGridBackground />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950/60 via-emerald-950/40 to-gray-950/90" />

        <Link to="/" className="relative z-10 w-fit">
          <span className="text-2xl font-black tracking-tight text-white drop-shadow-sm">
            manteo.ai
          </span>
        </Link>

        <div className="relative z-10 max-w-md">
          <h1 className="text-4xl font-black leading-tight tracking-tight text-white drop-shadow-sm">
            Earth data, without the headache.
          </h1>
          <ul className="mt-7 space-y-4">
            {heroPoints.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3.5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10 text-emerald-300 ring-1 ring-white/20 backdrop-blur-sm">
                  <Icon size={18} />
                </span>
                <span className="text-base leading-snug text-emerald-50/90">{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      <main className="flex min-h-screen flex-1 flex-col">
        <div className="flex flex-1 items-center justify-center px-6 py-12 sm:px-10">
          <div className={`w-full ${wide ? 'max-w-lg' : 'max-w-md'}`}>
            <div className="mb-10 flex justify-center lg:hidden">
              <img src="/logo.svg" alt="manteo.ai" className="h-11" />
            </div>
            {children}
          </div>
        </div>
        {footer && <div className="px-6 pb-6 sm:px-10">{footer}</div>}
      </main>
    </div>
  );
};

export default AuthLayout;
